import { HttpClient } from '@angular/common/http';
import { computed, inject } from '@angular/core';

import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';

import { CSVQuestionRow, Question, QuestionAnswered } from '@src/app-models';
import { getNextQuestionIndex, mapCsvRowToQuestion, normalize } from '@src/app-utils';

import Papa from 'papaparse';
import { catchError, EMPTY, map, pipe, switchMap, tap } from 'rxjs';

type State = {
  hasStarted: boolean;
  principles: Map<string, Question[]>;
  questions: Question[];
  currentQuestion?: QuestionAnswered;
  completedPrinciples: Record<string, QuestionAnswered>;
};

export const AppStore = signalStore(
  withState<State>(() => ({
    hasStarted: false,
    questions: [],
    currentQuestion: undefined,
    completedPrinciples: {},
    principles: new Map<string, Question[]>(),
  })),

  withMethods((store, http = inject(HttpClient)) => {
    const loadQuestions = rxMethod<void>(
      pipe(
        switchMap(() =>
          http.get('/assets/questions.csv', { responseType: 'text' }).pipe(
            map(text => {
              const { data } = Papa.parse<CSVQuestionRow>(text, {
                header: true,
                skipEmptyLines: true,
                transformHeader: h => h.trim(),
              });
              return data.map(mapCsvRowToQuestion);
            }),
            map(questions => {
              const principles = new Map<string, Question[]>();
              for (const q of questions) {
                let arr = principles.get(q.principe);
                if (!arr) {
                  arr = [];
                  principles.set(q.principe, arr);
                }
                arr.push(q);
              }
              return { questions, principles };
            }),
            tap(({ questions, principles }) => {
              patchState(store, {
                hasStarted: false,
                questions,
                currentQuestion: undefined,
                completedPrinciples: {},
                principles,
              });
            }),
            catchError(err => {
              console.error(err?.message ?? 'Error loading csv questions');
              return EMPTY;
            })
          )
        )
      )
    );

    const drawQuestion = (principe: string): void => {
      const p = normalize(principe);
      const currentQuestion = store.currentQuestion();

      if (currentQuestion?.principe === p) {
        return;
      }

      if (store.completedPrinciples()[p]) {
        patchState(store, { currentQuestion: store.completedPrinciples()[p] });
        return;
      }

      const principles = store.principles();
      const questionsFromPrinciple = principles.get(p) ?? [];

      const nextIndex = getNextQuestionIndex(p, questionsFromPrinciple.length);
      patchState(store, { currentQuestion: questionsFromPrinciple[nextIndex] });
    };

    const resolveChoice = (question: Question, choice: string): void => {
      if (!question || !choice) return;

      const isCorrect = question.answer === choice;
      const p = normalize(question.principe);

      patchState(store, {
        completedPrinciples: { ...store.completedPrinciples(), [p]: { ...question, isCorrect } },
        currentQuestion: { ...question, isCorrect },
      });
    };

    const startGame = (): void => {
      patchState(store, { hasStarted: true });
    };

    const restartGame = (): void => {
      patchState(store, { hasStarted: false, currentQuestion: undefined, completedPrinciples: {} });
    };

    const clearCurrentQuestion = (): void => {
      patchState(store, { currentQuestion: undefined });
    };

    return { loadQuestions, drawQuestion, resolveChoice, startGame, restartGame, clearCurrentQuestion };
  }),
  withComputed(store => ({
    score: computed(() => Object.values(store.completedPrinciples()).filter(v => v.isCorrect).length),
    availablePrinciples: computed(() => {
      return store.principles().size - Object.keys(store.completedPrinciples()).length;
    }),
    isEndgame: computed(() => store.principles().size === Object.keys(store.completedPrinciples()).length && store.principles().size > 0),
  })),
  withHooks(store => ({
    onInit() {
      store.loadQuestions();
    },
  }))
);
